import { useState } from "react";
import { isFunction } from "lodash";
import { t } from "@/i18n-js/instance";
import { Button } from "@circle-react-uikit/Button";
import { CommunityMemberPickerProvider } from "./CommunityMemberPickerContext";
import { Selection } from "./Selection";
import { SelectionModal } from "./SelectionModal";
import type { SummaryProps } from "./Summary";
import { Summary } from "./Summary";
import type { Value, Variant } from "./types";
import { VARIANTS } from "./types";

export interface CommunityMemberPickerComponentProps
  extends Omit<SummaryProps, "onClick" | "onChange"> {
  allowCurrentCommunityMember?: boolean;
  disableSearch?: boolean;
  excludedMemberIds?: number[];
  includeAllResultTypes?: boolean;
  modalSaveButtonLabel?: string;
  name: string;
  onChange: (value: Value) => void;
  onModalClose?: () => void;
  onModalSave?: (value: Value) => void;
  renderAfterSelection?: React.ReactNode;
  searchPlaceholder: string;
  selectionLabel: string;
  value: Value;
  variant: Variant;
}

export const CommunityMemberPickerComponent = ({
  onChange,
  value,
  name,
  selectionLabel,
  searchPlaceholder,
  buttonLabel,
  modalSaveButtonLabel,
  includeAllResultTypes = false,
  renderAfterSelection = null,
  variant = VARIANTS.SIMPLE,
  disableSearch,
  excludedMemberIds = [],
  allowCurrentCommunityMember = false,
  onModalClose,
  onModalSave,
  ...passThroughProps
}: CommunityMemberPickerComponentProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleModal = () => {
    setIsOpen(!isOpen);
    if (isOpen && isFunction(onModalClose)) {
      onModalClose();
    }
  };

  return (
    <CommunityMemberPickerProvider
      onChange={onChange}
      value={value}
      name={name}
      includeAllResultTypes={includeAllResultTypes}
      excludedMemberIds={excludedMemberIds}
      allowCurrentCommunityMember={allowCurrentCommunityMember}
      variant={variant}
      onModalSave={onModalSave}
    >
      {variant === VARIANTS.SIMPLE && (
        <Selection
          searchPlaceholder={searchPlaceholder}
          disableSearch={disableSearch}
        />
      )}
      {variant === VARIANTS.MODAL && (
        <>
          <Summary onClick={toggleModal} {...passThroughProps} />
          <SelectionModal
            isOpen={isOpen}
            onClose={toggleModal}
            selectionLabel={selectionLabel}
            searchPlaceholder={searchPlaceholder}
            modalSaveButtonLabel={modalSaveButtonLabel}
            renderAfterSelection={renderAfterSelection}
            disableSearch={disableSearch}
          />
        </>
      )}
      {variant === VARIANTS.BUTTON && (
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={toggleModal}
            className="min-w-[96px]"
          >
            {buttonLabel || t("add")}
          </Button>
          <SelectionModal
            isOpen={isOpen}
            onClose={toggleModal}
            selectionLabel={selectionLabel}
            searchPlaceholder={searchPlaceholder}
            modalSaveButtonLabel={modalSaveButtonLabel}
            renderAfterSelection={renderAfterSelection}
            disableSearch={disableSearch}
          />
        </>
      )}
    </CommunityMemberPickerProvider>
  );
};
